import {
  ArrowPathRoundedSquareIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  MegaphoneIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  SquaresPlusIcon,
  TruckIcon,
  UsersIcon,
  QueueListIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";
import { LinkProps, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { SettingsSupplierPicker } from "@/app/Settings/Settings/Components/SettingsSupplierPicker";
import { FC, Suspense } from "react";
import {
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarItemLink,
  SidebarLabel,
  SidebarSection,
} from "@/components/catalyst/sidebar";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";
import { FeatureFlagType } from "@/gql/graphql";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { useTranslations } from "use-intl";

type VisibleTo = "all" | "supplier" | "no-supplier";

type SettingNavItem = {
  name: string;
  to: LinkProps["to"];
  icon: FC<any>;
  visibleTo: VisibleTo;
  current: boolean;
};

export function SettingsScreen() {
  "use no memo";
  const t = useTranslations("settings.navigation");
  const matchRoute = useMatchRoute();
  const { supplier } = useSettingsSupplier();
  const { isAdmin } = useTenant();

  const subNavigation: SettingNavItem[] = [
    {
      name: "Select supplier",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
      icon: BuildingOffice2Icon,
      visibleTo: "no-supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
        fuzzy: false,
      }),
    },
    {
      name: t("general"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
      icon: Cog6ToothIcon,
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
        fuzzy: false,
      }),
    },
    {
      name: t("vehicles"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/vehicles",
      icon: TruckIcon,
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/vehicles",
        fuzzy: false,
      }),
    },
    {
      name: t("offices"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices",
      icon: MapPinIcon,
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices",
        fuzzy: false,
      }),
    },
    {
      name: t("insurance"),
      icon: ShieldCheckIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/insurance",
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/insurance",
        fuzzy: false,
      }),
    },
    {
      name: t("notifications"),
      icon: MegaphoneIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/notifications",
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/notifications",
        fuzzy: false,
      }),
    },
    {
      name: t("billing"),
      icon: CurrencyDollarIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing",
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing",
        fuzzy: true,
      }),
    },
    {
      name: t("integrations"),
      icon: SquaresPlusIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations",
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations",
        fuzzy: false,
      }),
    },
    ...(supplier?.email_on_line_sold
      ? [
          {
            name: t("activeLines"),
            icon: QueueListIcon,
            to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/active-lines",
            visibleTo: "supplier",
            current: !!matchRoute({
              to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/active-lines",
              fuzzy: false,
            }),
          } as SettingNavItem,
        ]
      : []),
  ];

  const gapRentalNavigation: SettingNavItem[] = [
    {
      name: "Gap rentals",
      icon: ArrowPathRoundedSquareIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/gap-rentals",
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/gap-rentals",
        fuzzy: false,
      }),
    },
  ];

  const accountNavigation: SettingNavItem[] = [
    {
      name: t("reporting"),
      icon: PresentationChartLineIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/reports",
      visibleTo: "all",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/reports",
        fuzzy: false,
      }),
    },
    {
      name: t("team"),
      icon: UsersIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/organisation",
      // The team is the signed-in user's own organisation, so for an admin it
      // is imoova's team rather than the supplier they picked.
      visibleTo: isAdmin ? "no-supplier" : "all",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/organisation",
        fuzzy: true,
      }),
    },
    {
      name: t("activity"),
      icon: ClockIcon,
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/activity",
      visibleTo: "supplier",
      current: !!matchRoute({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/activity",
        fuzzy: false,
      }),
    },
  ];

  const gapRentalsEnabled = useFeatureFlag(FeatureFlagType.GapRentals);

  return (
    <div className="scroll-gutter-stable h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-7xl lg:grid lg:grid-cols-12 lg:gap-x-5 lg:px-8 lg:py-12">
        <aside className="relative px-2 py-6 sm:px-6 lg:col-span-2 lg:px-0 lg:py-0">
          <Sidebar>
            <SidebarBody>
              <SidebarSection>
                {subNavigation
                  .filter((item) => {
                    if (item.visibleTo === "all") {
                      return true;
                    }

                    if (item.visibleTo === "supplier" && supplier) {
                      return true;
                    }

                    return item.visibleTo === "no-supplier" && !supplier;
                  })
                  .map((item) => (
                    <SidebarItemLink
                      key={item.name}
                      to={item.to}
                      search={true}
                      linkClassName="text-zinc-900 data-hover:bg-zinc-950/5 data-hover:*:data-[slot=icon]:fill-zinc-950 data-current:*:data-[slot=icon]:fill-black"
                      indicatorClassName="bg-gray-900"
                      activeProps={{
                        "aria-current": "page",
                      }}
                      current={item.current}
                    >
                      <item.icon className="size-4" />
                      <SidebarLabel>{item.name}</SidebarLabel>
                    </SidebarItemLink>
                  ))}

                {supplier && gapRentalsEnabled ? (
                  <>
                    <SidebarDivider />
                    {gapRentalNavigation.map((item) => (
                      <SidebarItemLink
                        key={item.name}
                        to={item.to}
                        search={true}
                        linkClassName="text-zinc-900 data-hover:bg-zinc-950/5 data-hover:*:data-[slot=icon]:fill-zinc-950 data-current:*:data-[slot=icon]:fill-black"
                        indicatorClassName="bg-gray-900"
                        activeProps={{
                          "aria-current": "page",
                        }}
                        current={item.current}
                      >
                        <item.icon className="size-4" />
                        <SidebarLabel>{item.name}</SidebarLabel>
                      </SidebarItemLink>
                    ))}
                  </>
                ) : null}

                <SidebarDivider />
                {accountNavigation
                  .filter((item) => {
                    if (item.visibleTo === "all") {
                      return true;
                    }

                    if (item.visibleTo === "supplier" && supplier) {
                      return true;
                    }

                    return item.visibleTo === "no-supplier" && !supplier;
                  })
                  .map((item) => (
                    <SidebarItemLink
                      key={item.name}
                      to={item.to}
                      search={true}
                      linkClassName="text-zinc-900 data-hover:bg-zinc-950/5 data-hover:*:data-[slot=icon]:fill-zinc-950 data-current:*:data-[slot=icon]:fill-black"
                      indicatorClassName="bg-gray-900"
                      activeProps={{
                        "aria-current": "page",
                      }}
                      current={item.current}
                    >
                      <item.icon className="size-4" />
                      <SidebarLabel>{item.name}</SidebarLabel>
                    </SidebarItemLink>
                  ))}
              </SidebarSection>
            </SidebarBody>
          </Sidebar>
        </aside>
        {/* Payment details */}
        <div className="p w-full space-y-6 sm:px-6 lg:col-span-9">
          <SettingsSupplierPicker />
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center">
                <Spinner className="size-5" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
